import { Controller } from "@hotwired/stimulus"

// Listens to stage:token-click events from the Stage controller and shows
// the appropriate Tavern dialogue. This is the first implementation of the
// "ScriptedDM" idea from ADR-0040 -- keep it simple: a click on an NPC opens
// that NPC's dialogue tree.
//
// Branching, typewriter, portraits, and voice-over are follow-on polish.

const ARCHIVIST_DIALOGUE = {
  greet_unknown: {
    lines: [
      "Another traveller.",
      "Don't mind the dust -- we don't get many new faces.",
      "You look new. What do we call you?",
    ],
    awaits_name: true,
  },
  ledger_prompt: {
    lines: ["{name}. Not bad.", "Shall I write your name in the ledger?"],
    choices: [
      { label: "Write it down.", effect: "signup" },
      { label: "Not yet -- let me look around.", effect: "dismiss" },
    ],
  },
  greet_returning: {
    lines: ["{name}. Back again.", "Take your time. The hearth doesn't judge."],
  },
}

const SCHOLAR_DIALOGUE = {
  default: {
    lines: [
      "A scholar at the table, nose deep in a ledger.",
      "\"Don't suppose you've seen a merchant named Eren? Or was it Aren? That's rather the problem.\"",
    ],
  },
}

export default class extends Controller {
  static values = {
    archivistTokenId: Number,
    scholarTokenId: Number,
    signedIn: Boolean,
    progressName: String,
  }

  connect() {
    this.dialogueRoot = document.getElementById("tavern-dialogue")
    this.busy = false
  }

  handleTokenClick(event) {
    if (this.busy) return
    const id = event.detail.id
    if (id === this.archivistTokenIdValue) this.flowArchivist()
    else if (id === this.scholarTokenIdValue) this.flowScholar()
  }

  async flowArchivist() {
    this.busy = true
    try {
      const hasName = this.progressNameValue && this.progressNameValue.length > 0
      if (hasName) {
        await this.present(ARCHIVIST_DIALOGUE.greet_returning, { name: this.progressNameValue })
        return
      }
      const nameResult = await this.present(ARCHIVIST_DIALOGUE.greet_unknown)
      if (nameResult?.type !== "name") return
      this.progressNameValue = nameResult.value
      await this.postProgress({ name: nameResult.value })

      const choice = await this.present(ARCHIVIST_DIALOGUE.ledger_prompt, { name: nameResult.value })
      if (choice?.effect === "signup" && !this.signedInValue) {
        window.location.href = "/users/sign_up"
      }
    } finally {
      this.busy = false
    }
  }

  async flowScholar() {
    this.busy = true
    try {
      await this.present(SCHOLAR_DIALOGUE.default)
    } finally {
      this.busy = false
    }
  }

  // ── Low-level: render a dialogue node and await the user's response ──
  present(node, ctx = {}) {
    return new Promise((resolve) => {
      this.dialogueRoot.innerHTML = ""
      this.dialogueRoot.classList.remove("hidden")

      const box = document.createElement("div")
      box.className = "tavern-dialogue-box"

      for (const line of node.lines) {
        const p = document.createElement("p")
        p.textContent = this.format(line, ctx)
        box.appendChild(p)
      }

      if (node.awaits_name) {
        const input = document.createElement("input")
        input.type = "text"
        input.maxLength = 24
        input.placeholder = "Your name"
        input.className = "tavern-dialogue-input"
        const submit = document.createElement("button")
        submit.textContent = "Tell them"
        submit.className = "tavern-dialogue-button"
        box.appendChild(input)
        box.appendChild(submit)
        this.dialogueRoot.appendChild(box)
        const finish = () => {
          const value = input.value.trim()
          if (!value) { input.focus(); return }
          this.close()
          resolve({ type: "name", value })
        }
        submit.addEventListener("click", finish)
        input.addEventListener("keydown", (e) => { if (e.key === "Enter") finish() })
        setTimeout(() => input.focus(), 50)
        return
      }

      if (node.choices?.length) {
        const choices = document.createElement("div")
        choices.className = "tavern-dialogue-choices"
        for (const [i, choice] of node.choices.entries()) {
          const btn = document.createElement("button")
          btn.textContent = choice.label
          btn.className = "tavern-dialogue-button"
          btn.dataset.index = String(i)
          choices.appendChild(btn)
        }
        box.appendChild(choices)
        this.dialogueRoot.appendChild(box)
        choices.addEventListener("click", (e) => {
          const btn = e.target.closest("button")
          if (!btn) return
          const choice = node.choices[Number(btn.dataset.index)]
          this.close()
          resolve({ type: "choice", effect: choice.effect })
        })
        return
      }

      // Plain dismiss
      const dismiss = document.createElement("button")
      dismiss.textContent = "..."
      dismiss.className = "tavern-dialogue-button"
      box.appendChild(dismiss)
      this.dialogueRoot.appendChild(box)
      dismiss.addEventListener("click", () => { this.close(); resolve({ type: "dismiss" }) })
    })
  }

  close() {
    this.dialogueRoot.classList.add("hidden")
    this.dialogueRoot.innerHTML = ""
  }

  format(text, ctx) {
    return text.replace(/\{(\w+)\}/g, (_, k) => ctx[k] ?? this[k + "Value"] ?? "")
  }

  // Persist progress
  async postProgress(payload) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch("/tavern/progress", {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrf || "",
        },
        body: JSON.stringify(payload),
      })
    } catch (_) { /* best-effort */ }
  }
}
